import { useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL, MCP_ENABLED } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { availableMcpServersQueryKeys } from '@/hooks/mcp/types'
import { normalizeMCPToolsets } from '@/hooks/mcp/utils'

import type { UUID } from '@/types/Id'
import type { MCPServer } from '@/types/MCPServer'

interface UseMCPServersArgs {
  orgId: UUID | ''
}

const fetchMCPServers = async (
  accessToken: string | undefined | null,
  orgId: UUID | '',
): Promise<MCPServer[]> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  }

  if (orgId !== '') {
    headers['X-Organization-Id'] = orgId
  }

  const res: Response = await fetch(`${API_URL}/api/v1/mcp-servers`, {
    headers,
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: `Failed to fetch MCP servers: ${res.statusText}`,
      cause: {
        status: res.status,
      },
    })
  }

  const data = await res.json()
  return data.data.map((server: any) => {
    const convertedServer = convertDatesInObject(server)
    return {
      ...convertedServer,
      toolsets: normalizeMCPToolsets(convertedServer.toolsets),
    }
  })
}

export const useMCPServers = ({ orgId }: UseMCPServersArgs) => {
  const { accessToken } = useAuthInfo()

  return useQuery<MCPServer[], Error>({
    queryKey: availableMcpServersQueryKeys.organization({ orgId: orgId }),
    queryFn: () => fetchMCPServers(accessToken, orgId),
    enabled: !!accessToken && MCP_ENABLED,
  })
}
